export const DATA_VIEW_FRAME_ATTRIBUTE = 'data-data-view-frame';
export const DATA_VIEW_FRAME_TOP_ATTRIBUTE = 'data-data-view-frame-top';
export const DATA_VIEW_FRAME_BODY_ATTRIBUTE = 'data-data-view-frame-body';
export const DATA_VIEW_FRAME_FOOTER_ATTRIBUTE = 'data-data-view-frame-footer';

export interface DataViewFrameMetrics {
  readonly bodyHeight: number;
  readonly bodyRenderHeight: number;
  readonly footerHeight: number;
  readonly topHeight: number;
  readonly totalHeight: number;
}

function getDataViewFrameMarkedElement(
  host: ParentNode | null | undefined,
  attribute: string
): HTMLElement | undefined {
  if (!host) {
    return undefined;
  }
  return (
    Array.from(host.children).find(
      (child): child is HTMLElement =>
        child instanceof HTMLElement && child.getAttribute(attribute) === 'true'
    ) ?? undefined
  );
}

export function getDataViewFrameElement(
  host: ParentNode | null | undefined
): HTMLElement | undefined {
  if (!host) {
    return undefined;
  }
  if (
    host instanceof HTMLElement &&
    host.getAttribute(DATA_VIEW_FRAME_ATTRIBUTE) === 'true'
  ) {
    return host;
  }
  return (
    host.querySelector<HTMLElement>(`[${DATA_VIEW_FRAME_ATTRIBUTE}="true"]`) ??
    undefined
  );
}

export function getDataViewFrameTopElement(
  frame: ParentNode | null | undefined
): HTMLElement | undefined {
  return getDataViewFrameMarkedElement(frame, DATA_VIEW_FRAME_TOP_ATTRIBUTE);
}

export function getDataViewFrameBodyElement(
  frame: ParentNode | null | undefined
): HTMLElement | undefined {
  return getDataViewFrameMarkedElement(frame, DATA_VIEW_FRAME_BODY_ATTRIBUTE);
}

export function getDataViewFrameFooterElement(
  frame: ParentNode | null | undefined
): HTMLElement | undefined {
  return getDataViewFrameMarkedElement(frame, DATA_VIEW_FRAME_FOOTER_ATTRIBUTE);
}

function measureFlowElementHeight(
  element: HTMLElement | null | undefined,
  visualScale: number
): number {
  if (!element) {
    return 0;
  }
  const position = getComputedStyle(element).position;
  if (position === 'absolute' || position === 'fixed') {
    return 0;
  }
  return element.getBoundingClientRect().height / (visualScale || 1);
}

export function measureDataViewFrameMetrics({
  bodyIntrinsicHeight = 0,
  bodyRenderHeight = 0,
  frame,
  paddingHeight = 0,
  visualScale,
}: {
  bodyIntrinsicHeight?: number;
  bodyRenderHeight?: number;
  frame: HTMLElement | null | undefined;
  paddingHeight?: number;
  visualScale: number;
}): DataViewFrameMetrics {
  const scale = visualScale || 1;
  const topHeight = measureFlowElementHeight(
    getDataViewFrameTopElement(frame),
    scale
  );
  const bodyHeight =
    bodyIntrinsicHeight ||
    measureFlowElementHeight(getDataViewFrameBodyElement(frame), scale);
  const footerHeight = measureFlowElementHeight(
    getDataViewFrameFooterElement(frame),
    scale
  );
  return {
    bodyHeight,
    bodyRenderHeight,
    footerHeight,
    topHeight,
    totalHeight: topHeight + bodyHeight + footerHeight + paddingHeight,
  };
}
